import React from 'react';
import { contactVideos } from '../data/galleryImages';
import { MapPin, Phone, Clock, Play, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const ContactoPage = () => {
  const socialMedia = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      action: 'Chatear',
      url: 'https://wa.me/51959389799',
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      name: 'Instagram',
      icon: '📷',
      action: 'Seguir',
      url: 'https://www.instagram.com/tqmfloreria.pe',
      color: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
    },
    {
      name: 'Facebook',
      icon: '👍',
      action: 'Me gusta',
      url: 'https://www.facebook.com/tqmflores',
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      name: 'TikTok',
      icon: '🎵',
      action: 'Seguir',
      url: 'https://www.tiktok.com/@tqmfloreria',
      color: 'bg-black hover:bg-gray-900'
    }
  ];

  const steps = [
    { number: '1', title: 'Contáctanos', description: 'Por WhatsApp o redes sociales' },
    { number: '2', title: 'Cuéntanos', description: 'Qué necesitas y para cuándo' },
    { number: '3', title: 'Personalizamos', description: 'Creamos tu arreglo perfecto' },
    { number: '4', title: 'Entregamos', description: 'En menos de 3 horas' }
  ];

  return (
    <div className="min-h-screen bg-pink-50 pt-16 md:pt-20">
      {/* Hero Section */}
      <section 
        className="py-12 md:py-16 px-4"
        style={{
          backgroundImage: 'url(https://imagedelivery.net/xaKlCos5cTg_1RWzIu_h-A/e9bba638-d807-427f-bea3-a775809cad00/publicContain)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-pink-900 mb-4 md:mb-6">
            ¡Contáctanos y Haz tu Pedido!
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-pink-800">
            Estamos listos para crear el arreglo perfecto para ti
          </p>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-pink-900 mb-2 md:mb-4">
            Síguenos en Redes 📱
          </h2>
          <p className="text-center text-pink-700 text-base md:text-lg mb-8 md:mb-12">Elige tu red social favorita</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {socialMedia.map((social, index) => (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${social.color} text-white p-6 md:p-8 rounded-2xl text-center shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex flex-col items-center justify-center gap-3`}
              >
                {typeof social.icon === 'string' ? (
                  <span className="text-4xl md:text-5xl">{social.icon}</span>
                ) : (
                  <social.icon className="w-10 h-10 md:w-12 md:h-12" />
                )}
                <h3 className="text-lg md:text-xl font-bold">{social.name}</h3>
                <p className="text-sm md:text-base opacity-90">{social.action}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* How to Order Section */}
      <section className="py-12 md:py-16 px-4 bg-pink-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-pink-900 mb-12 md:mb-16">
            ¿Cómo Hacer tu Pedido? 🌸
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-12">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-pink-600 text-white w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-3xl md:text-4xl font-bold mx-auto mb-4 shadow-lg">
                  {step.number}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-pink-900 mb-2">{step.title}</h3>
                <p className="text-sm md:text-base text-pink-700">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              onClick={() => window.open('https://wa.me/51959389799?text=Hola,%20quiero%20hacer%20un%20pedido', '_blank')}
              className="bg-pink-600 hover:bg-pink-700 text-white px-6 md:px-10 py-4 md:py-6 text-lg md:text-xl rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              ¡Hacer mi Pedido!
            </Button>
          </div>
        </div>
      </section>

      {/* Videos Section */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-pink-900 mb-12">
            Videos de Nuestros Trabajos 🎥
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {contactVideos.map((video) => (
              <a
                key={video.id}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer aspect-[4/5]"
              >
                <img 
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                  <div className="bg-white/90 rounded-full p-4 md:p-6 transform group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-8 h-8 md:w-12 md:h-12 text-pink-600 fill-current" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-white font-semibold text-base md:text-lg">{video.title}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 md:py-16 px-4 bg-pink-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-pink-900 mb-12">
            Información de Contacto 📍
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl p-6 md:p-8 text-center shadow-lg">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
                <Clock className="w-8 h-8 text-pink-600" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-pink-900 mb-2">Horarios</h3>
              <p className="text-pink-700 text-base md:text-lg">Lun-Sáb: 8AM - 9PM</p>
              <p className="text-pink-600 text-sm md:text-base mt-2">Domingo: Consultar</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 text-center shadow-lg">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
                <Phone className="w-8 h-8 text-pink-600" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-pink-900 mb-2">Teléfono</h3>
              <a href="tel:+51959389799" className="text-pink-700 text-lg md:text-xl hover:text-pink-900 font-semibold">
                +51 959 389 799
              </a>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 text-center shadow-lg">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
                <MapPin className="w-8 h-8 text-pink-600" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-pink-900 mb-2">Ubicación</h3>
              <p className="text-pink-700 text-base md:text-lg mb-2">Arequipa, Perú</p>
              <p className="text-pink-600 text-sm md:text-base">Delivery en toda Arequipa</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-pink-900 mb-8 md:mb-12">
            Encuéntranos
          </h2>
          <p className="text-center text-pink-700 text-base md:text-lg mb-8">93 Av. Lambramani, José Luis Bustamante, Arequipa</p>
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!4v1735968123456!6m8!1m7!1sIHSzn1kmQeQRKZXZGXoiPw!2m2!1d-16.41467376891595!2d-71.51928096264982!3f48.24!4f-2.88!5f0.7105427569768294"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación Tqm Florería"
            ></iframe>
          </div>
          <div className="text-center mt-6 md:mt-8">
            <a
              href="https://www.google.com/maps/place/Tqm+Floreria/@-16.4146191,-71.5194612,3a,38.2y,48.24h,87.12t/data=!3m7!1e1!3m5!1sIHSzn1kmQeQRKZXZGXoiPw!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D2.883724168331838%26panoid%3DIHSzn1kmQeQRKZXZGXoiPw%26yaw%3D48.24431306649364!7i16384!8i8192!4m6!3m5!1s0x91424b1ca8c411bb:0x6066bc23ec5a2d93!8m2!3d-16.4146738!4d-71.519281!16s%2Fg%2F11fj40mfjj"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-pink-600 hover:bg-pink-700 text-white px-6 md:px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Ver en Google Maps
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactoPage;